import React, { FC } from "react";
import FadeIn from "react-fade-in";
import { FiMusic, FiZap, FiClock, FiLayers, FiFileText } from "react-icons/fi";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ScatterController,
} from "chart.js";
import { Line, Bar, Scatter, Chart } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ScatterController,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface TranscriptionOverviewProps {
  startDate: string;
  endDate: string;
  refreshTrigger?: number;
  data?: any;
}

const TranscriptionOverview: FC<TranscriptionOverviewProps> = ({
  startDate,
  endDate,
  refreshTrigger,
  data,
}) => {
  const formatAudioDuration = (minutesVal: any) => {
    if (minutesVal === undefined || minutesVal === null || minutesVal === "-") return "-";
    const totalMins = parseFloat(minutesVal);
    if (isNaN(totalMins)) return String(minutesVal);
    const hrs = Math.floor(totalMins / 60);
    const mins = Math.round(totalMins % 60);
    if (hrs > 0) {
      return `${hrs} hr${hrs > 1 ? 's' : ''} ${mins} min${mins !== 1 ? 's' : ''}`;
    }
    return `${mins} min${mins !== 1 ? 's' : ''}`;
  };

  const formatThroughput = (val: any) => {
    if (val === undefined || val === null || val === "-") return "-";
    const valStr = String(val);
    if (valStr.includes("%")) return valStr;
    const parsed = parseFloat(valStr);
    if (isNaN(parsed)) return valStr;
    return `${parsed.toFixed(1)}%`;
  };

  const formatLatency = (val: any) => {
    if (val === undefined || val === null || val === "-") return "-";
    const valStr = String(val);
    if (valStr.toLowerCase().endsWith("s")) return valStr;
    const parsed = parseFloat(valStr);
    if (isNaN(parsed)) return valStr;
    return `${parsed.toFixed(1)}s`;
  };

  const formatSecondsToMS = (secVal: number) => {
    const roundedSec = Math.round(secVal);
    const mins = Math.floor(roundedSec / 60);
    const secs = roundedSec % 60;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(mins)}:${pad(secs)}`;
  };

  // KPI data
  const kpiData = [
    {
      label: "Total Jobs",
      value: data?.total_records ?? "-",
      icon: <FiFileText size={20} />,
      bgColor: "bg-blue-50 text-blue-600",
      iconColor: "group-hover:text-blue-700",
    },
    {
      label: "Total Audio",
      value: formatAudioDuration(data?.stats?.total_audio_minutes),
      icon: <FiMusic size={20} />,
      bgColor: "bg-emerald-50 text-emerald-600",
      iconColor: "group-hover:text-emerald-700",
    },
    {
      label: "Throughput",
      value: formatThroughput(data?.stats?.completion_rate),
      icon: <FiZap size={20} />,
      bgColor: "bg-rose-50 text-rose-600",
      iconColor: "group-hover:text-rose-700",
    },
    {
      label: "Avg Latency",
      value: formatLatency(data?.stats?.avg_latency),
      icon: <FiClock size={20} />,
      bgColor: "bg-indigo-50 text-indigo-600",
      iconColor: "group-hover:text-indigo-700",
    },
    {
      label: "Avg Queue Wait",
      value: formatLatency(data?.stats?.avg_queue_wait),
      icon: <FiLayers size={20} />,
      bgColor: "bg-amber-50 text-amber-600",
      iconColor: "group-hover:text-amber-700",
    },
  ];

  const formatTimeLabel = (dtStr: string) => {
    if (!dtStr) return "";
    const parts = dtStr.split(" ");
    return parts[1] || dtStr;
  };

  const hourlyMetrics = data?.hourly_metrics ?? [];
  const hourlyLabels = hourlyMetrics.map((row: any) => formatTimeLabel(row.time));
  
  const throughputDataset = hourlyMetrics.map((row: any) => row.finished_stt_count ?? row.jobs ?? 0);
  const sttQueueWaitDataset = hourlyMetrics.map((row: any) => row.avg_stt_queue_wait ?? 0);
  const analyticsQueueWaitDataset = hourlyMetrics.map((row: any) => row.avg_analytics_queue_wait ?? 0);
  const webhookQueueWaitDataset = hourlyMetrics.map((row: any) => row.avg_webhook_wait ?? 0);
  
  const durationDistDataset = hourlyMetrics.map((row: any) => row.total_audio_minutes ?? row.finished_audio_minutes ?? 0);

  const avgWebhook = hourlyMetrics.length
    ? hourlyMetrics.reduce((acc: number, r: any) => acc + (r.avg_webhook_wait ?? 0), 0) / hourlyMetrics.length
    : 0;
  const avgAnalytics = hourlyMetrics.length
    ? hourlyMetrics.reduce((acc: number, r: any) => acc + (r.avg_analytics_processing_time ?? 0), 0) / hourlyMetrics.length
    : 0;
  const avgSTT = hourlyMetrics.length
    ? hourlyMetrics.reduce((acc: number, r: any) => acc + (r.avg_stt_processing_time ?? 0), 0) / hourlyMetrics.length
    : 0;
  
  const processingTimeDataObj = [avgWebhook, avgAnalytics, avgSTT];

  const hasSlimScatter = Array.isArray(data?.slim_scatter) && data.slim_scatter.length > 0;
  const scatterPoints = hasSlimScatter
    ? data.slim_scatter.map((row: any) => ({
        x: row.audio_duration ?? 0,
        y: row.stt_processing_time ?? 0,
      }))
    : hourlyMetrics.map((row: any) => {
        const count = row.jobs || row.finished_stt_count || 1;
        const avgDurationSec = ((row.total_audio_minutes ?? 0) * 60) / count;
        return {
          x: parseFloat(avgDurationSec.toFixed(1)),
          y: parseFloat((row.avg_stt_processing_time ?? 0).toFixed(1)),
        };
      });

  const labels = hourlyMetrics.length ? hourlyLabels : (data?.throughput_hourly?.labels ?? []);

  // 1. Jobs Throughput (Hourly)
  const throughputData = {
    labels,
    datasets: [
      {
        fill: true,
        label: "Throughput",
        data: hourlyMetrics.length ? throughputDataset : (data?.throughput_hourly?.data ?? []),
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        tension: 0.4,
        pointRadius: 0,
      },
    ],
  };

  // 2. Queue Wait Times (Hourly)
  const queueWaitData = {
    labels: hourlyMetrics.length ? hourlyLabels : (data?.queue_wait_hourly?.labels ?? []),
    datasets: [
      {
        label: "STT Queue",
        data: hourlyMetrics.length ? sttQueueWaitDataset : (data?.queue_wait_hourly?.stt ?? []),
        borderColor: "#4f46e5",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
      {
        label: "Analytics Queue",
        data: hourlyMetrics.length ? analyticsQueueWaitDataset : (data?.queue_wait_hourly?.analytics ?? []),
        borderColor: "#f59e0b",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
      {
        label: "Webhook Delivery",
        data: hourlyMetrics.length ? webhookQueueWaitDataset : (data?.queue_wait_hourly?.webhook ?? []),
        borderColor: "#10b981",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
    ],
  };

  // 3. Audio Duration Distribution
  const hasHistogram = Array.isArray(data?.duration_histogram) && data.duration_histogram.length > 0;
  const durationLabels = hasHistogram
    ? data.duration_histogram.map((row: any) => `${formatSecondsToMS(row.range_start)} - ${formatSecondsToMS(row.range_end)}`)
    : (data?.duration_distribution?.labels ?? []);
  const durationDistData = {
    labels: durationLabels,
    datasets: [
      {
        label: "Audio Samples",
        data: hasHistogram
          ? data.duration_histogram.map((row: any) => row.count ?? 0)
          : (data?.duration_distribution?.data ?? []),
        backgroundColor: "#8b5cf6",
        borderRadius: 4,
        barThickness: 30,
      },
    ],
  };

  // 4. Avg processing Time Comparison (Horizontal Bar)
  const processingTimeData = {
    labels: ["Webhook", "Analytics", "STT"],
    datasets: [
      {
        label: "Time (s)",
        data: hourlyMetrics.length ? processingTimeDataObj : (data?.processing_time_comparison?.data ?? []),
        backgroundColor: "#ec4899",
        borderRadius: 4,
        barThickness: 25,
      },
    ],
  };

  // 5. Duration vs STT processing Time
  const scatterData = {
    datasets: [
      {
        label: "Duration vs STT Time",
        data: hasSlimScatter || hourlyMetrics.length ? scatterPoints : (data?.duration_vs_stt?.data ?? []),
        backgroundColor: "#0ea5e9",
      },
    ],
  };

  // 6. Latency Percentiles (P50, P95, P99)
  const latencyData = {
    labels: data?.latency_percentiles?.labels ?? [],
    datasets: [
      {
        label: "P50",
        data: data?.latency_percentiles?.p50 ?? [],
        borderColor: "#10b981",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
      {
        label: "P95",
        data: data?.latency_percentiles?.p95 ?? [],
        borderColor: "#f59e0b",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
      {
        label: "P99",
        data: data?.latency_percentiles?.p99 ?? [],
        borderColor: "#ef4444",
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12 },
        },
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          borderDash: [5, 5],
          color: "rgba(0, 0, 0, 0.05)",
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const horizontalBarOptions = {
    ...chartOptions,
    indexAxis: "y" as const,
    scales: {
      ...chartOptions.scales,
      x: {
        beginAtZero: true,
        title: { display: true, text: "s", position: "end" as const },
      },
    },
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {kpiData.map((kpi, idx) => (
          <FadeIn key={idx} delay={idx * 50}>
            <div className="bg-white p-6 rounded-[24px] shadow-md hover:shadow-md border border-gray-100/90 flex flex-col justify-between h-full group hover:-translate-y-1 transition-all duration-300 ease-out cursor-default">
              <div className="flex justify-between items-start mb-4">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest leading-none">
                  {kpi.label}
                </div>
                <div className={`p-3.5 rounded-2xl shadow-inner ${kpi.bgColor} ${kpi.iconColor} transition-transform duration-300 group-hover:scale-110`}>
                  {kpi.icon}
                </div>
              </div>
              <div className="mt-2">
                <h3 className="text-3xl font-extrabold text-slate-800 tracking-tight leading-none">
                  {kpi.value}
                </h3>
              </div>
            </div>
          </FadeIn>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
        {/* Row 1 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">
            Jobs Throughput (Hourly)
          </h3>
          <div className="h-[300px]">
            <Line data={throughputData} options={chartOptions} />
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">
            Queue Wait Times (Hourly)
          </h3>
          <div className="h-[300px]">
            <Line data={queueWaitData} options={chartOptions} />
          </div>
        </div>

        {/* Row 2 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">
            Audio Duration Distribution
          </h3>
          <div className="h-[300px]">
            <Bar data={durationDistData} options={chartOptions} />
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">
            Avg processing Time Comparison
          </h3>
          <div className="h-[300px]">
            <Bar
              data={processingTimeData}
              options={horizontalBarOptions as any}
            />
          </div>
        </div>

        {/* Row 3 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 lg:col-span-2">
          <h3 className="text-base font-bold text-gray-800 mb-6">
            Duration vs STT processing Time
          </h3>
          <div className="h-[300px]">
            <Scatter
              data={scatterData}
              options={
                {
                  ...chartOptions,
                  scales: {
                    ...chartOptions.scales,
                    x: {
                      type: "linear",
                      position: "bottom",
                      title: {
                        display: true,
                        text: "Audio Duration (s)",
                        font: { size: 10 },
                      },
                    },
                    y: {
                      title: {
                        display: true,
                        text: "STT Time (s)",
                        font: { size: 10 },
                      },
                    },
                  },
                } as any
              }
            />
          </div>
        </div>
        {/* <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-base font-bold text-gray-800 mb-6">Latency Percentiles (P50, P95, P99)</h3>
            <div className="h-[300px]">
                <Line data={latencyData} options={chartOptions} />
            </div>
        </div> */}
      </div>
    </div>
  );
};

export default TranscriptionOverview;
